import { useEffect, useRef } from 'react';
import { getPortalSessionId } from '../services/api';
import type { SupportActivityState, SupportRealtimeActivity } from '../utils/supportActivity';

const API_PREFIX = import.meta.env.VITE_API_PREFIX || '/user/api';

export type SupportRealtimeSnapshot = {
  type: 'ticket_snapshot';
  ticket_id: number;
  user_id: number;
  ticket?: {
    id: number;
    user_id: number;
    subject: string;
    status: 'open' | 'answered' | 'closed';
    created_at: string;
    updated_at: string;
    last_message_preview?: string;
    last_message_id?: number;
    last_message_type?: 'text' | 'image' | 'video' | 'voice' | 'album' | 'sticker' | 'gif';
    last_message_media_count?: number;
    last_message_author_role?: 'user' | 'support' | null;
    username?: string;
    first_name?: string;
  };
  detail?: {
    id: number;
    user_id: number;
    subject: string;
    status: 'open' | 'answered' | 'closed';
    created_at: string;
    updated_at: string;
    messages: {
      id: number;
      ticket_id: number;
      author_telegram_id: number;
      author_role: 'user' | 'support';
      message_type?: 'text' | 'image' | 'video' | 'voice' | 'album' | 'sticker' | 'gif';
      body: string;
      created_at: string;
      attachment_name?: string;
      attachment_mime?: string;
      attachment_size?: number;
      duration_ms?: number;
      has_attachment?: boolean;
    }[];
    username?: string;
    first_name?: string;
  };
};

export type SupportRealtimeEvent = SupportRealtimeSnapshot | SupportRealtimeActivity;

function wsUrl(): string {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const sessionId = getPortalSessionId();
  const qs = sessionId ? `?session_id=${encodeURIComponent(sessionId)}` : '';
  return `${proto}//${window.location.host}${API_PREFIX}/support/ws${qs}`;
}

export type UseSupportRealtimeOptions = {
  enabled?: boolean;
  /** Called on initial connect and after each reconnect — use to catch up missed messages. */
  onConnect?: () => void;
};

export type SupportRealtimeControls = {
  sendActivity: (ticketId: number, state: SupportActivityState) => void;
  sendReceipt: (ticketId: number, kind: 'delivered' | 'read', upToMessageId: number) => void;
};

/** Live support updates via WebSocket (Redis fanout). */
export function useSupportRealtime(
  onEvent: (event: SupportRealtimeEvent) => void,
  options: UseSupportRealtimeOptions | boolean = true,
): SupportRealtimeControls {
  const enabled = typeof options === 'boolean' ? options : (options.enabled ?? true);
  const handlerRef = useRef(onEvent);
  handlerRef.current = onEvent;
  const onConnectRef = useRef(
    typeof options === 'boolean' ? undefined : options.onConnect,
  );
  onConnectRef.current = typeof options === 'boolean' ? undefined : options.onConnect;
  const socketRef = useRef<WebSocket | null>(null);
  const lastActivityRef = useRef<{ ticketId: number; state: SupportActivityState; at: number } | null>(
    null,
  );

  useEffect(() => {
    if (!enabled) return;

    let closed = false;
    let socket: WebSocket | null = null;
    let retryTimer: number | null = null;
    let attempt = 0;

    const connect = () => {
      if (closed) return;
      const ws = new WebSocket(wsUrl());
      socket = ws;
      socketRef.current = ws;

      ws.onopen = () => {
        attempt = 0;
        onConnectRef.current?.();
        const pending = lastActivityRef.current;
        if (pending && pending.state !== 'idle') {
          try {
            ws.send(
              JSON.stringify({
                type: 'activity',
                ticket_id: pending.ticketId,
                state: pending.state,
              }),
            );
          } catch {
            /* ignore */
          }
        }
      };

      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(String(ev.data)) as { type?: string };
          if (data?.type === 'ping') {
            ws.send('ping');
            return;
          }
          if (data?.type === 'pong') return;
          if (data?.type === 'ticket_snapshot' || data?.type === 'activity') {
            handlerRef.current(data as SupportRealtimeEvent);
          }
        } catch {
          /* ignore */
        }
      };

      ws.onclose = () => {
        if (socketRef.current === ws) socketRef.current = null;
        socket = null;
        if (closed) return;
        attempt += 1;
        const delay = Math.min(10000, 500 * 2 ** Math.min(attempt, 4));
        retryTimer = window.setTimeout(connect, delay);
      };

      ws.onerror = () => {
        try {
          ws.close();
        } catch {
          /* ignore */
        }
      };
    };

    connect();

    return () => {
      closed = true;
      if (retryTimer != null) window.clearTimeout(retryTimer);
      try {
        socket?.close();
      } catch {
        /* ignore */
      }
      if (socketRef.current === socket) socketRef.current = null;
    };
  }, [enabled]);

  return {
    sendActivity: (ticketId: number, state: SupportActivityState) => {
      lastActivityRef.current = { ticketId, state, at: Date.now() };
      const ws = socketRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      try {
        ws.send(JSON.stringify({ type: 'activity', ticket_id: ticketId, state }));
      } catch {
        /* ignore */
      }
    },
    sendReceipt: (ticketId: number, kind: 'delivered' | 'read', upToMessageId: number) => {
      const ws = socketRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      try {
        ws.send(
          JSON.stringify({
            type: 'receipt',
            ticket_id: ticketId,
            kind,
            up_to_message_id: upToMessageId,
          }),
        );
      } catch {
        /* ignore */
      }
    },
  };
}
